import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewCriteriaKeys, reviewTemplateKeys } from "@/lib/query";
import type {
  ReviewCriterionCreateInput,
  ReviewCriterionUpdateInput,
} from "@/schemas/review-criteria";

/** One question a review form can ask. */
export interface ReviewCriterionRow {
  id: string;
  title: string;
  helpText: string | null;
  categoryId: string;
  categoryName: string;
  categoryColor: string | null;
  /** Set when the question is linked to a competency, which brings expectations. */
  competencyId: string | null;
  competencyName: string | null;
  isDefault: boolean;
  defaultWeight: number;
  sortOrder: number;
  isActive: boolean;
  usedByForms: number;
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => null);
  const message =
    body && typeof body === "object" && "error" in body && typeof body.error === "string"
      ? body.error
      : fallback;
  return new Error(message);
}

export function useReviewCriteria(activeOnly = false) {
  return useQuery({
    queryKey: reviewCriteriaKeys.list(activeOnly),
    queryFn: async (): Promise<ReviewCriterionRow[]> => {
      const res = await fetch(`/api/review-criteria${activeOnly ? "?active=true" : ""}`);
      if (!res.ok) throw await failure(res, "Couldn't load the questions");
      return (await res.json()).data;
    },
  });
}

/**
 * Invalidate the catalogue AND the form being edited.
 *
 * A question created from inside the form builder has to appear in both: the
 * picker it was added from, and the form it is about to be put on.
 */
function useCriteriaMutation<TInput>(
  run: (input: TInput) => Promise<Response>,
  fallback: string,
  success: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => {
      const res = await run(input);
      if (!res.ok) throw await failure(res, fallback);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewCriteriaKeys.all });
      queryClient.invalidateQueries({ queryKey: reviewTemplateKeys.all });
      toast.success(success);
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useCreateReviewCriterion() {
  return useCriteriaMutation<ReviewCriterionCreateInput>(
    (input) =>
      fetch("/api/review-criteria", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    "Couldn't add that question",
    "Question added",
  );
}

export function useUpdateReviewCriterion() {
  return useCriteriaMutation<ReviewCriterionUpdateInput & { id: string }>(
    ({ id, ...input }) =>
      fetch(`/api/review-criteria/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    "Couldn't save that question",
    "Question saved",
  );
}

export function useDeleteReviewCriterion() {
  return useCriteriaMutation<string>(
    (id) => fetch(`/api/review-criteria/${id}`, { method: "DELETE" }),
    "Couldn't remove that question",
    "Question removed",
  );
}

export function useReorderReviewCriterion() {
  return useCriteriaMutation<{ id: string; direction: "up" | "down" }>(
    ({ id, direction }) =>
      fetch(`/api/review-criteria/${id}/reorder`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      }),
    "Couldn't move that question",
    "Moved",
  );
}
